import HeadMetadata from 'components/shared/HeadMetadata';
import { useI18n } from 'hooks/useI18n';
import { usePageTrack } from 'hooks/usePageTrack';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Script from 'next/script';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { getPublicPath } from 'utils/paths';
import { avoidRateLimit } from 'utils/rate-limit';
import { WP_REST_API_Categories, WP_REST_API_Posts } from 'wp-types';
import BlogComponent from '../../components/marketingSite/blog/BlogComponent';

type BlogPageProps = {
  posts?: WP_REST_API_Posts;
  categories?: WP_REST_API_Categories;
  totalPosts: number;
  totalPages: number;
};

const BlogPage: React.FC<BlogPageProps> = (props) => {
  const { t } = useI18n('blog');

  usePageTrack('Blog - Page Loaded');

  return (
    <>
      <HeadMetadata
        title={t('pageTitle')}
        desc={t('pageDescription')}
        imgUrl={getPublicPath('/images/card_blog.png')}
        url={APP_URLS.blog}
        indexable={true}
      />

      <Script
        id="blogMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />

      <BlogComponent {...props} currentPage={0} />
    </>
  );
};

export default BlogPage;

export const getStaticProps: GetStaticProps<BlogPageProps> = async ({
  locale
}) => {
  await avoidRateLimit();
  const responsePosts = await WPService.getPosts({
    locale: locale || 'en'
  });

  await avoidRateLimit();
  const categories = (await WPService.getCategories(locale || 'en')).filter(
    (c) =>
      c.count > 0 &&
      c.slug !== 'uncategorized' &&
      c.slug !== 'uncategorised' &&
      c.slug !== 'oculto' &&
      c.slug !== 'sin-categorizar' &&
      c.slug !== 'all' &&
      c.slug !== 'todo-el-blog'
  );

  return {
    props: {
      ...(await serverSideTranslations(locale || '', [
        'common',
        'topbar',
        'blog',
        'footer'
      ])),
      posts: responsePosts?.data,
      totalPosts: Number(responsePosts?.headers.get('x-wp-total') || ''),
      totalPages: Number(responsePosts?.headers.get('x-wp-totalpages') || ''),
      categories
    },
    revalidate: 10
  };
};
